/**
 * TanStack Query option factories. Keeping them here means query keys are
 * declared once and both SPAs invalidate the same cache entries.
 */
import {
  infiniteQueryOptions,
  queryOptions,
  type QueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import type { ApiClient } from './client';
import type {
  Allocation,
  AuthMetadata,
  Attestation,
  BaselineAsset,
  BaselineOverrideCreate,
  BaselineOverrideList,
  BaselineOverrideUpdate,
  BaselineState,
  BaselineSummary,
  EffectiveBaseline,
  RuntimeBaselineSummary,
  TimeToPatch,
  BomEntry,
  BomSummary,
  BomVersion,
  Box,
  BomKind,
  CapacitySummary,
  Cluster,
  ClusterInput,
  DemoRun,
  DemoRunAction,
  DemoScenarioList,
  ClusterProvider,
  ClusterSnapshot,
  Device,
  DeviceInput,
  DeviceRole,
  Finding,
  FindingAction,
  FindingEvent,
  FindingKind,
  FindingResolution,
  K8sNode,
  NodeState,
  Page,
  Posture,
  QueueFinding,
  Vulnerability,
  VulnerabilityDetail,
  VulnerabilitySummary,
  PosturePeriod,
  ClusterMaxEdition,
  CriterionReadiness,
  FleetReadiness,
  ScopeReadiness,
  Scan,
  Sensor,
  SensorCommand,
  SensorCommandRequest,
  SensorEnrollment,
  SensorEventList,
  SensorInput,
  SensorState,
  SensorSummary,
  SensorTarget,
  TargetBinding,
  BindTargetInput,
  IngestSummary,
  SiemDestination,
  SiemDestinationInput,
  SiemTestResult,
  CreatedSensor,
  Session,
  User,
  UserRole,
  Severity,
  SlurmBuild,
  SlurmBuildDrift,
  SlurmBuildKind,
  SlurmCapacity,
  SlurmCluster,
  SlurmClusterInput,
  SlurmJob,
  SlurmJobDetail,
  SlurmJobState,
  SlurmNode,
  SlurmNodeDetail,
  SlurmNodeState,
  SlurmPartition,
  SlurmPartitionState,
  SlurmProvider,
  SlurmQueue,
  SlurmReservation,
  SlurmReservationState,
  SlurmSnapshot,
  SlurmSnapshotStatus,
  SlurmSummary,
  Summary,
  Workload,
  WorkloadImage,
  WorkloadState,
} from './types';

export const queryKeys = {
  box: (id: string) => ['boxes', id] as const,
  session: ['session'] as const,
  authMetadata: ['auth', 'metadata'] as const,
  summary: ['summary'] as const,
  devices: (filter: DeviceListFilter = {}) => ['devices', filter] as const,
  device: (id: string) => ['devices', id] as const,
  deviceFeeds: (id: string) => ['devices', id, 'feeds'] as const,
  ingestSummary: ['ingest', 'summary'] as const,
  components: (filter: ComponentListFilter = {}) => ['components', filter] as const,
  componentSummary: (filter: ComponentSummaryFilter = {}) =>
    ['components', 'summary', filter] as const,
  componentVersions: (filter: ComponentVersionFilter = {}) =>
    ['components', 'versions', filter] as const,
  scans: (filter: ScanListFilter = {}) => ['scans', filter] as const,
  users: ['users'] as const,
  userList: (filter: UserListFilter = {}) => ['users', 'list', filter] as const,
  attestations: (filter: AttestationListFilter = {}) => ['attestations', filter] as const,
  posture: (days: PosturePeriod) => ['posture', days] as const,
  vulnerabilities: (filter: VulnerabilityListFilter = {}) =>
    ['vulnerabilities', 'list', filter] as const,
  vulnerabilitySummary: ['vulnerabilities', 'summary'] as const,
  vulnerability: (cveId: string) => ['vulnerabilities', 'cve', cveId] as const,
  queue: (filter: QueueFindingFilter = {}) => ['queue', 'list', filter] as const,
  queueFinding: (id: string) => ['queue', 'finding', id] as const,
  queueEvents: (id: string) => ['queue', 'finding', id, 'events'] as const,
  clustermaxCriteria: (edition: string) => ['clustermax', 'criteria', edition] as const,
  readiness: (days: PosturePeriod) => ['clustermax', 'readiness', 'fleet', days] as const,
  scopeReadiness: (scopeId: string, days: PosturePeriod) =>
    ['clustermax', 'readiness', 'scope', scopeId, days] as const,
  criterionReadiness: (scopeId: string, criterionId: string) =>
    ['clustermax', 'readiness', 'scope', scopeId, 'criterion', criterionId] as const,
  /** Prefixes: every entry of a family, whatever its filter. */
  componentsRoot: ['components'] as const,
  postureRoot: ['posture'] as const,
  queueRoot: ['queue'] as const,
  vulnerabilitiesRoot: ['vulnerabilities'] as const,
  /** Every readiness answer: they are all computed from the findings. */
  readinessRoot: ['clustermax', 'readiness'] as const,
};

/*
 * The server caches the aggregate endpoints (summary, component summary and
 * versions, posture) and clears a tenant's cache on any operator write, so a
 * refetch right after a successful mutation is correct. These two helpers are
 * the one place that names which client entries those endpoints back; a
 * mutation calls the helper rather than listing keys.
 */

/**
 * invalidateInventoryAggregates marks stale everything computed from the
 * inventory in bulk: the dashboard tiles (`/summary`) and every component
 * summary, version and list entry (`['components']` is their shared prefix).
 * Call it after anything that adds or removes devices, boxes, components,
 * software or scans, or edits the catalog.
 */
export function invalidateInventoryAggregates(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
    queryClient.invalidateQueries({ queryKey: queryKeys.componentsRoot }),
  ]).then(() => undefined);
}

/**
 * invalidatePosture marks the executive posture score stale for every period.
 * Call it after anything that changes findings.
 */
export function invalidatePosture(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: queryKeys.postureRoot });
}

export interface Pagination {
  limit?: number;
  offset?: number;
}

export interface DeviceListFilter extends Pagination {
  role?: DeviceRole;
  vendor?: string;
  q?: string;
}

export interface ComponentListFilter extends Pagination {
  boxId?: string;
  deviceId?: string;
  kind?: BomKind;
  name?: string;
  /** An exact version match: the query an advisory prompts. */
  version?: string;
}

export interface ComponentSummaryFilter {
  kind?: BomKind;
  name?: string;
}

export interface ComponentVersionFilter extends Pagination, ComponentSummaryFilter {
  /** Only models running more than one version. */
  drifted?: boolean;
}

export interface ScanListFilter extends Pagination {
  deviceId?: string;
}

export interface AttestationListFilter extends Pagination {
  deviceId?: string;
}

export interface UserListFilter extends Pagination {
  q?: string;
  role?: UserRole;
  includeDisabled?: boolean;
}

/** One box of the structured inventory, with its placement. */
export const boxQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: queryKeys.box(id),
    queryFn: ({ signal }) => api.get<Box>(`/inventory/boxes/${id}`, undefined, { signal }),
    staleTime: 30_000,
  });

export const sessionQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: queryKeys.session,
    queryFn: () => api.get<Session>('/auth/session'),
    // The session is the gate for the whole console; never serve it stale and
    // never retry a 401 into a redirect loop.
    staleTime: 30_000,
    retry: false,
  });

/** The public auth metadata: which identity providers can sign an operator in.
 * Both SPAs read it; it changes only when the server is reconfigured. */
export const authMetadataQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: queryKeys.authMetadata,
    queryFn: () => api.get<AuthMetadata>('/auth/metadata'),
    staleTime: 5 * 60_000,
  });

export const summaryQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: queryKeys.summary,
    queryFn: () => api.get<Summary>('/summary'),
  });

/** The trend lengths the posture view offers, shortest first. */
export const posturePeriods = [30, 90, 365] as const satisfies readonly PosturePeriod[];

/** The period the posture view shows when none is chosen: the API's default. */
export const defaultPosturePeriod: PosturePeriod = 30;

/** isPosturePeriod narrows an untrusted value (a URL search param) to a
 * period the API accepts; anything else would be refused with a 400. */
export function isPosturePeriod(value: unknown): value is PosturePeriod {
  return posturePeriods.some((period) => period === value);
}

/**
 * The executive posture view: score, trend, top issues. It is served by the
 * findings service, which the proxy routes /api/v1/posture to, through the
 * same client and cookie as the API. Computed on read from the whole backlog
 * and refreshed by a producer every 15 minutes, so a minute of staleness costs
 * nothing.
 */
export const postureQuery = (api: ApiClient, days: PosturePeriod) =>
  queryOptions({
    queryKey: queryKeys.posture(days),
    queryFn: ({ signal }) => api.get<Posture>('/posture', { days }, { signal }),
    staleTime: 60_000,
  });

export const devicesQuery = (api: ApiClient, filter: DeviceListFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.devices(filter),
    queryFn: () => api.get<Page<Device>>('/devices', { ...filter }),
  });

export const deviceQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: queryKeys.device(id),
    queryFn: () => api.get<Device>(`/devices/${id}`),
  });

export const componentsQuery = (api: ApiClient, filter: ComponentListFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.components(filter),
    queryFn: () => api.get<Page<BomEntry>>('/components', { ...filter }),
  });

export const componentSummaryQuery = (api: ApiClient, filter: ComponentSummaryFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.componentSummary(filter),
    queryFn: () => api.get<BomSummary>('/components/summary', { ...filter }),
  });

/** nextOffset pages an offset-paginated list until total is reached. */
const nextOffset = <T>(last: Page<T>) =>
  last.offset + last.items.length < last.total && last.items.length > 0
    ? last.offset + last.items.length
    : undefined;

/** The flat bill of materials, a page at a time, for a "Load more" list. */
export const componentsInfiniteQuery = (api: ApiClient, filter: ComponentListFilter = {}) =>
  infiniteQueryOptions({
    queryKey: [...queryKeys.components(filter), 'pages'] as const,
    queryFn: ({ pageParam }) =>
      api.get<Page<BomEntry>>('/components', { ...filter, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
  });

/** The bill of materials grouped by model and version, a page at a time. */
export const componentVersionsInfiniteQuery = (
  api: ApiClient,
  filter: ComponentVersionFilter = {},
) =>
  infiniteQueryOptions({
    queryKey: [...queryKeys.componentVersions(filter), 'pages'] as const,
    queryFn: ({ pageParam }) =>
      api.get<Page<BomVersion>>('/components/versions', { ...filter, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
  });

export const scansQuery = (api: ApiClient, filter: ScanListFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.scans(filter),
    queryFn: () => api.get<Page<Scan>>('/scans', { ...filter }),
  });

export const attestationsQuery = (api: ApiClient, filter: AttestationListFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.attestations(filter),
    queryFn: () => api.get<Page<Attestation>>('/attestations', { ...filter }),
  });

export const logoutMutation = (api: ApiClient): UseMutationOptions<void> => ({
  mutationFn: () => api.post('/auth/logout'),
});

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

/** Changing one's own password ends every session, this one included. */
export const changePasswordMutation = (
  api: ApiClient,
): UseMutationOptions<void, Error, ChangePasswordInput> => ({
  mutationFn: (input) => api.put('/auth/password', input),
});

/* --- User management (authservice, admin only) ---------------------------- */

export const usersQuery = (api: ApiClient, filter: UserListFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.userList(filter),
    queryFn: () => api.get<Page<User>>('/auth/users', { ...filter }),
    // Keep the previous page on screen while the next one loads.
    placeholderData: (previous) => previous,
  });

export interface CreateUserInput {
  email: string;
  displayName: string;
  role: UserRole;
  /** Optional: omit it for a user who signs in through an identity provider. */
  password?: string;
  locale?: string;
}

export const createUserMutation = (
  api: ApiClient,
): UseMutationOptions<User, Error, CreateUserInput> => ({
  mutationFn: (input) => api.post<User>('/auth/users', input),
});

export interface UpdateUserInput {
  id: string;
  displayName?: string;
  role?: UserRole;
  disabled?: boolean;
  locale?: string;
}

export const updateUserMutation = (
  api: ApiClient,
): UseMutationOptions<User, Error, UpdateUserInput> => ({
  mutationFn: ({ id, ...patch }) => api.patch<User>(`/auth/users/${id}`, patch),
});

export interface ResetUserPasswordInput {
  id: string;
  newPassword: string;
}

export const resetUserPasswordMutation = (
  api: ApiClient,
): UseMutationOptions<void, Error, ResetUserPasswordInput> => ({
  mutationFn: ({ id, newPassword }) => api.put(`/auth/users/${id}/password`, { newPassword }),
});

export const revokeUserSessionsMutation = (
  api: ApiClient,
): UseMutationOptions<void, Error, string> => ({
  mutationFn: (id) => api.post(`/auth/users/${id}/revoke-sessions`),
});

export interface UnlinkIdentityInput {
  id: string;
  provider: string;
}

/** Unlinks one identity provider from a user (404 not_found if not linked). */
export const unlinkIdentityMutation = (
  api: ApiClient,
): UseMutationOptions<void, Error, UnlinkIdentityInput> => ({
  mutationFn: ({ id, provider }) =>
    api.delete(`/auth/users/${id}/identities/${encodeURIComponent(provider)}`),
});

export const deleteUserMutation = (api: ApiClient): UseMutationOptions<void, Error, string> => ({
  mutationFn: (id) => api.delete(`/auth/users/${id}`),
});

export const createDeviceMutation = (
  api: ApiClient,
): UseMutationOptions<Device, Error, DeviceInput> => ({
  mutationFn: (input) => api.post<Device>('/devices', input),
});

/**
 * A queued scan changes scan history and, once it lands, the bill of
 * materials, so the scan lists and the inventory aggregates are stale.
 */
export const triggerScanMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<Scan, Error, string> => ({
  mutationFn: (deviceId) => api.post<Scan>(`/devices/${deviceId}/scans`),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['scans'] }),
      invalidateInventoryAggregates(queryClient),
    ]);
  },
});

/* --- SLURM inventory ------------------------------------------------------ */

export const slurmKeys = {
  summary: ['slurm', 'summary'] as const,
  clusters: (filter: SlurmClusterListFilter = {}) => ['slurm', 'clusters', filter] as const,
  cluster: (id: string) => ['slurm', 'clusters', id] as const,
  capacity: (clusterId: string) => ['slurm', 'clusters', clusterId, 'capacity'] as const,
  buildDrift: (clusterId: string) => ['slurm', 'clusters', clusterId, 'build-drift'] as const,
  queue: (clusterId: string) => ['slurm', 'clusters', clusterId, 'queue'] as const,
  nodes: (filter: SlurmNodeListFilter = {}) => ['slurm', 'nodes', filter] as const,
  node: (id: string) => ['slurm', 'nodes', id] as const,
  partitions: (filter: SlurmPartitionListFilter = {}) => ['slurm', 'partitions', filter] as const,
  reservations: (filter: SlurmReservationListFilter = {}) =>
    ['slurm', 'reservations', filter] as const,
  jobs: (filter: SlurmJobListFilter = {}) => ['slurm', 'jobs', filter] as const,
  job: (id: string) => ['slurm', 'jobs', id] as const,
  builds: (filter: SlurmBuildListFilter = {}) => ['slurm', 'builds', filter] as const,
  build: (id: string) => ['slurm', 'builds', id] as const,
  snapshots: (filter: SlurmSnapshotListFilter = {}) => ['slurm', 'snapshots', filter] as const,
};

/*
 * Every SLURM filter field admits an explicit undefined, because these
 * filters come straight from URL search where an absent parameter is exactly
 * that. Requiring the caller to omit the key instead would mean rebuilding
 * the object with conditional spreads at every call site.
 */

export interface SlurmClusterListFilter extends Pagination {
  provider?: SlurmProvider | undefined;
  q?: string | undefined;
}

export interface SlurmNodeListFilter extends Pagination {
  clusterId?: string | undefined;
  state?: SlurmNodeState | undefined;
  /** Omit for no constraint: `false` asks only for the unschedulable nodes,
   * which is a different question from "all nodes". */
  available?: boolean | undefined;
  partition?: string | undefined;
  gpuModel?: string | undefined;
  buildId?: string | undefined;
  feature?: string | undefined;
  q?: string | undefined;
}

export interface SlurmPartitionListFilter extends Pagination {
  clusterId?: string | undefined;
  state?: SlurmPartitionState | undefined;
}

export interface SlurmReservationListFilter extends Pagination {
  clusterId?: string | undefined;
  state?: SlurmReservationState | undefined;
}

export interface SlurmJobListFilter extends Pagination {
  clusterId?: string | undefined;
  state?: SlurmJobState | undefined;
  /** Named shortcut for the several states that make up "on the cluster now". */
  activity?: 'active' | 'finished' | 'all' | undefined;
  account?: string | undefined;
  user?: string | undefined;
  partition?: string | undefined;
  qos?: string | undefined;
  nodeName?: string | undefined;
  jobId?: number | undefined;
  q?: string | undefined;
}

export interface SlurmBuildListFilter extends Pagination {
  clusterId?: string | undefined;
  kind?: SlurmBuildKind | undefined;
  q?: string | undefined;
}

export interface SlurmSnapshotListFilter extends Pagination {
  clusterId?: string | undefined;
  status?: SlurmSnapshotStatus | undefined;
}

export const slurmSummaryQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: slurmKeys.summary,
    queryFn: () => api.get<SlurmSummary>('/slurm/summary'),
  });

export const slurmClustersQuery = (api: ApiClient, filter: SlurmClusterListFilter = {}) =>
  queryOptions({
    queryKey: slurmKeys.clusters(filter),
    queryFn: () => api.get<Page<SlurmCluster>>('/slurm/clusters', { ...filter }),
  });

export const slurmClusterQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: slurmKeys.cluster(id),
    queryFn: () => api.get<SlurmCluster>(`/slurm/clusters/${id}`),
  });

export const slurmCapacityQuery = (api: ApiClient, clusterId: string) =>
  queryOptions({
    queryKey: slurmKeys.capacity(clusterId),
    queryFn: () => api.get<SlurmCapacity>(`/slurm/clusters/${clusterId}/capacity`),
  });

export const slurmBuildDriftQuery = (api: ApiClient, clusterId: string) =>
  queryOptions({
    queryKey: slurmKeys.buildDrift(clusterId),
    queryFn: () => api.get<SlurmBuildDrift>(`/slurm/clusters/${clusterId}/build-drift`),
  });

export const slurmQueueQuery = (api: ApiClient, clusterId: string) =>
  queryOptions({
    queryKey: slurmKeys.queue(clusterId),
    queryFn: () => api.get<SlurmQueue>(`/slurm/clusters/${clusterId}/queue`),
  });

export const slurmNodesQuery = (api: ApiClient, filter: SlurmNodeListFilter = {}) =>
  queryOptions({
    queryKey: slurmKeys.nodes(filter),
    queryFn: () => api.get<Page<SlurmNode>>('/slurm/nodes', { ...filter }),
  });

export const slurmNodeQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: slurmKeys.node(id),
    queryFn: () => api.get<SlurmNodeDetail>(`/slurm/nodes/${id}`),
  });

export const slurmPartitionsQuery = (api: ApiClient, filter: SlurmPartitionListFilter = {}) =>
  queryOptions({
    queryKey: slurmKeys.partitions(filter),
    queryFn: () => api.get<Page<SlurmPartition>>('/slurm/partitions', { ...filter }),
  });

export const slurmReservationsQuery = (api: ApiClient, filter: SlurmReservationListFilter = {}) =>
  queryOptions({
    queryKey: slurmKeys.reservations(filter),
    queryFn: () => api.get<Page<SlurmReservation>>('/slurm/reservations', { ...filter }),
  });

export const slurmJobsQuery = (api: ApiClient, filter: SlurmJobListFilter = {}) =>
  queryOptions({
    queryKey: slurmKeys.jobs(filter),
    queryFn: () => api.get<Page<SlurmJob>>('/slurm/jobs', { ...filter }),
  });

export const slurmJobQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: slurmKeys.job(id),
    queryFn: () => api.get<SlurmJobDetail>(`/slurm/jobs/${id}`),
  });

export const slurmBuildsQuery = (api: ApiClient, filter: SlurmBuildListFilter = {}) =>
  queryOptions({
    queryKey: slurmKeys.builds(filter),
    queryFn: () => api.get<Page<SlurmBuild>>('/slurm/builds', { ...filter }),
  });

export const slurmSnapshotsQuery = (api: ApiClient, filter: SlurmSnapshotListFilter = {}) =>
  queryOptions({
    queryKey: slurmKeys.snapshots(filter),
    queryFn: () => api.get<Page<SlurmSnapshot>>('/slurm/snapshots', { ...filter }),
  });

export const createSlurmClusterMutation = (
  api: ApiClient,
): UseMutationOptions<SlurmCluster, Error, SlurmClusterInput> => ({
  mutationFn: (input) => api.post<SlurmCluster>('/slurm/clusters', input),
});

export interface UpdateSlurmClusterInput extends SlurmClusterInput {
  id: string;
}

export const updateSlurmClusterMutation = (
  api: ApiClient,
): UseMutationOptions<SlurmCluster, Error, UpdateSlurmClusterInput> => ({
  mutationFn: ({ id, ...input }) => api.put<SlurmCluster>(`/slurm/clusters/${id}`, input),
});

/* --- Kubernetes inventory -------------------------------------------------- */

export interface ClusterListFilter extends Pagination {
  provider?: ClusterProvider;
  region?: string;
  q?: string;
}

export interface K8sNodeListFilter extends Pagination {
  clusterId?: string;
  state?: NodeState;
  pool?: string;
  /** Accelerator model as the device plugin reports it, e.g. "mi300x". */
  accelerator?: string;
  /** Restrict to the part of the fleet that carries accelerators. */
  gpusOnly?: boolean;
  q?: string;
}

export interface WorkloadListFilter extends Pagination {
  clusterId?: string;
  namespace?: string;
  queue?: string;
  account?: string;
  state?: WorkloadState;
  gpusOnly?: boolean;
  q?: string;
}

export interface ImageListFilter extends Pagination {
  clusterId?: string;
  repository?: string;
  digest?: string;
  revision?: string;
  /** Only references with no digest: the builds nobody can reproduce. */
  unpinnedOnly?: boolean;
  q?: string;
}

export interface FindingListFilter extends Pagination {
  clusterId?: string;
  kind?: FindingKind;
  severity?: Severity;
}

export interface AllocationListFilter extends Pagination {
  clusterId?: string;
  nodeId?: string;
  workloadId?: string;
}

export const k8sKeys = {
  clusters: (filter: ClusterListFilter = {}) => ['k8s', 'clusters', filter] as const,
  cluster: (id: string) => ['k8s', 'clusters', id] as const,
  snapshots: (clusterId: string) => ['k8s', 'clusters', clusterId, 'snapshots'] as const,
  capacity: (clusterId = '') => ['k8s', 'capacity', clusterId] as const,
  nodes: (filter: K8sNodeListFilter = {}) => ['k8s', 'nodes', filter] as const,
  node: (id: string) => ['k8s', 'nodes', id] as const,
  workloads: (filter: WorkloadListFilter = {}) => ['k8s', 'workloads', filter] as const,
  images: (filter: ImageListFilter = {}) => ['k8s', 'images', filter] as const,
  allocations: (filter: AllocationListFilter = {}) => ['k8s', 'allocations', filter] as const,
  findings: (filter: FindingListFilter = {}) => ['k8s', 'findings', filter] as const,
};

export const clustersQuery = (api: ApiClient, filter: ClusterListFilter = {}) =>
  queryOptions({
    queryKey: k8sKeys.clusters(filter),
    queryFn: () => api.get<Page<Cluster>>('/k8s/clusters', { ...filter }),
  });

export const clusterQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: k8sKeys.cluster(id),
    queryFn: () => api.get<Cluster>(`/k8s/clusters/${id}`),
  });

export const clusterSnapshotsQuery = (api: ApiClient, clusterId: string, limit = 10) =>
  queryOptions({
    queryKey: k8sKeys.snapshots(clusterId),
    queryFn: () =>
      api.get<Page<ClusterSnapshot>>(`/k8s/clusters/${clusterId}/snapshots`, { limit }),
  });

/** Omitting clusterId aggregates the whole fleet, which is what the overview
 * leads with. */
export const k8sCapacityQuery = (api: ApiClient, clusterId?: string) =>
  queryOptions({
    queryKey: k8sKeys.capacity(clusterId ?? ''),
    queryFn: () => api.get<CapacitySummary>('/k8s/capacity', clusterId ? { clusterId } : {}),
  });

export const k8sNodesQuery = (api: ApiClient, filter: K8sNodeListFilter = {}) =>
  queryOptions({
    queryKey: k8sKeys.nodes(filter),
    queryFn: () => api.get<Page<K8sNode>>('/k8s/nodes', { ...filter }),
  });

export const k8sNodeQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: k8sKeys.node(id),
    queryFn: () => api.get<K8sNode>(`/k8s/nodes/${id}`),
  });

export const workloadsQuery = (api: ApiClient, filter: WorkloadListFilter = {}) =>
  queryOptions({
    queryKey: k8sKeys.workloads(filter),
    queryFn: () => api.get<Page<Workload>>('/k8s/workloads', { ...filter }),
  });

export const k8sImagesQuery = (api: ApiClient, filter: ImageListFilter = {}) =>
  queryOptions({
    queryKey: k8sKeys.images(filter),
    queryFn: () => api.get<Page<WorkloadImage>>('/k8s/images', { ...filter }),
  });

export const allocationsQuery = (api: ApiClient, filter: AllocationListFilter = {}) =>
  queryOptions({
    queryKey: k8sKeys.allocations(filter),
    queryFn: () => api.get<Page<Allocation>>('/k8s/allocations', { ...filter }),
  });

export const findingsQuery = (api: ApiClient, filter: FindingListFilter = {}) =>
  queryOptions({
    queryKey: k8sKeys.findings(filter),
    queryFn: () => api.get<Page<Finding>>('/k8s/findings', { ...filter }),
  });

export const createClusterMutation = (
  api: ApiClient,
): UseMutationOptions<Cluster, Error, ClusterInput> => ({
  mutationFn: (input) => api.post<Cluster>('/k8s/clusters', input),
});

/* --- Demo scenarios ------------------------------------------------------- */

export const demoKeys = {
  scenarios: ['demo', 'scenarios'] as const,
};

/** How often a running demo load is polled. A large load runs for minutes and
 * moves in visible steps; faster than this only loads the API. */
const DEMO_RUN_POLL_MS = 1_500;

/**
 * The scenario catalog and the latest run. It polls only while a run is in
 * progress, and stops as soon as the run reaches a terminal state. A paused
 * run (its operator's session ended) is in progress: the poll is what carries
 * a live session to the demo service, and so what resumes it.
 */
export const demoScenariosQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: demoKeys.scenarios,
    queryFn: () => api.get<DemoScenarioList>('/demo/scenarios'),
    staleTime: 5_000,
    refetchInterval: (query) => {
      const state = query.state.data?.run?.state;
      return state === 'running' || state === 'paused' ? DEMO_RUN_POLL_MS : false;
    },
  });

export interface DemoRunInput {
  scenario: string;
  action: DemoRunAction;
}

export const startDemoRunMutation = (
  api: ApiClient,
): UseMutationOptions<DemoRun, Error, DemoRunInput> => ({
  mutationFn: ({ scenario, action }) =>
    api.post<DemoRun>(`/demo/scenarios/${encodeURIComponent(scenario)}/${action}`),
});

/**
 * invalidateDemoRunData marks stale every query family a finished demo run can
 * change. A load creates devices (and through their scans, boxes, components
 * and attestations) and registers Kubernetes and SLURM clusters, and the
 * findings follow; a clear removes the same. The session and the scenario
 * list itself are left alone.
 */
export function invalidateDemoRunData(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    invalidateInventoryAggregates(queryClient),
    invalidatePosture(queryClient),
    queryClient.invalidateQueries({ queryKey: ['devices'] }),
    queryClient.invalidateQueries({ queryKey: ['scans'] }),
    queryClient.invalidateQueries({ queryKey: ['attestations'] }),
    queryClient.invalidateQueries({ queryKey: ['k8s'] }),
    queryClient.invalidateQueries({ queryKey: ['slurm'] }),
    queryClient.invalidateQueries({ queryKey: ['consistency'] }),
    queryClient.invalidateQueries({ queryKey: ['handovers'] }),
  ]).then(() => undefined);
}

/* --- SIEM export ------------------------------------------------------------ */

export const siemKeys = {
  destinations: ['siem', 'destinations'] as const,
};

/** How often the destination list is refreshed while it is open, so the queue
 * counts and health follow the deliveries. Paused in a background tab. */
const SIEM_POLL_MS = 15_000;

/** The configured destinations with the state of each one's delivery queue. */
export const siemDestinationsQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: siemKeys.destinations,
    queryFn: () => api.get<{ items: SiemDestination[] }>('/siem/destinations'),
    staleTime: 10_000,
    refetchInterval: SIEM_POLL_MS,
  });

export interface SaveSiemDestinationInput {
  /** Present to replace that destination; absent to create one. */
  id?: string;
  input: SiemDestinationInput;
}

export const saveSiemDestinationMutation = (
  api: ApiClient,
): UseMutationOptions<SiemDestination, Error, SaveSiemDestinationInput> => ({
  mutationFn: ({ id, input }) =>
    id === undefined
      ? api.post<SiemDestination>('/siem/destinations', input)
      : api.put<SiemDestination>(`/siem/destinations/${encodeURIComponent(id)}`, input),
});

export const deleteSiemDestinationMutation = (
  api: ApiClient,
): UseMutationOptions<unknown, Error, string> => ({
  mutationFn: (id) => api.delete(`/siem/destinations/${encodeURIComponent(id)}`),
});

/** Sends one test event. A refusal by Splunk is a 200 with ok false. */
export const testSiemDestinationMutation = (
  api: ApiClient,
): UseMutationOptions<SiemTestResult, Error, string> => ({
  mutationFn: (id) => api.post<SiemTestResult>(`/siem/destinations/${encodeURIComponent(id)}/test`),
});

/** Puts a destination's dead events back in its queue. */
export const retryDeadSiemEventsMutation = (
  api: ApiClient,
): UseMutationOptions<{ requeued: number }, Error, string> => ({
  mutationFn: (id) =>
    api.post<{ requeued: number }>(`/siem/destinations/${encodeURIComponent(id)}/retry-dead`),
});

/* --- Sensors and relays ---------------------------------------------------- */

/**
 * The sensor keys are hierarchical so one prefix invalidates a family:
 * `detail(id)` covers the sensor, its targets and its commands, while
 * `commandsAll(id)` covers only the command history and `lists` only the
 * list views.
 */
export const sensorKeys = {
  all: ['sensors'] as const,
  lists: ['sensors', 'list'] as const,
  list: (filter: SensorListFilter = {}) => ['sensors', 'list', filter] as const,
  summary: ['sensors', 'summary'] as const,
  detail: (id: string) => ['sensors', 'detail', id] as const,
  targets: (id: string) => ['sensors', 'detail', id, 'targets'] as const,
  events: (id: string, limit: number) => ['sensors', 'detail', id, 'events', limit] as const,
  commandsAll: (id: string) => ['sensors', 'detail', id, 'commands'] as const,
  commands: (id: string, filter: Pagination = {}) =>
    ['sensors', 'detail', id, 'commands', 'list', filter] as const,
  command: (id: string, commandId: string) =>
    ['sensors', 'detail', id, 'commands', 'one', commandId] as const,
};

/** How often to re-read a command that has not finished. The sensor
 * long-polls for up to 25 s, so a faster interval buys nothing. */
export const sensorCommandPollMs = 3000;

/** isSensorCommandPending reports a command that has not reached a terminal
 * state: the sensor has yet to collect it, or to answer. */
export function isSensorCommandPending(command: SensorCommand): boolean {
  return command.state === 'queued' || command.state === 'delivered';
}

/** Every sensor state, in the order the console lists them. */
export const sensorStates = [
  'connected',
  'disconnected',
  'pending',
  'disabled',
  'revoked',
] as const satisfies readonly SensorState[];

/** The sensor list's filters, as `GET /sensors` accepts them. */
export interface SensorListFilter extends Pagination {
  /** Repeatable: any of these states. */
  state?: SensorState[];
  /** Decommissioned sensors are hidden unless asked for, or unless `state`
   * includes `revoked`. */
  includeRevoked?: boolean;
}

/**
 * How often the fleet views re-read a sensor's connection state. The server
 * calls a sensor disconnected two minutes after its last report, so a
 * quarter-minute keeps the console well inside that window without
 * hammering it. Only observed queries poll, and not in a background tab.
 */
export const sensorRefreshMs = 15_000;

export const sensorsQuery = (api: ApiClient, filter: SensorListFilter = {}) =>
  queryOptions({
    queryKey: sensorKeys.list(filter),
    queryFn: () => api.get<Page<Sensor>>('/sensors', { ...filter }),
    refetchInterval: sensorRefreshMs,
  });

export const sensorSummaryQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: sensorKeys.summary,
    queryFn: () => api.get<SensorSummary>('/sensors/summary'),
    refetchInterval: sensorRefreshMs,
  });

/** The sensor's own polling is the page's business (it speeds up while a
 * command is in flight), so this factory sets none. */
export const sensorQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: sensorKeys.detail(id),
    queryFn: () => api.get<Sensor>(`/sensors/${id}`),
  });

/**
 * A sensor's history, newest first, a page at a time: each "load older"
 * asks for the events before the oldest one shown. A page shorter than the
 * limit is the last. The key holds the limit because it decides where every
 * page after the first starts.
 */
export const sensorEventsQuery = (api: ApiClient, id: string, limit = 50) =>
  infiniteQueryOptions({
    queryKey: sensorKeys.events(id, limit),
    queryFn: ({ pageParam }) =>
      api.get<SensorEventList>(`/sensors/${id}/events`, {
        limit,
        ...(pageParam === undefined ? {} : { before: pageParam }),
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) =>
      last.items.length < limit ? undefined : last.items[last.items.length - 1]?.id,
    refetchInterval: sensorRefreshMs,
  });

export const sensorTargetsQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: sensorKeys.targets(id),
    queryFn: () => api.get<Page<SensorTarget>>(`/sensors/${id}/targets`, { limit: 500 }),
  });

/** The sensor targets that feed a device: at most one per collector. */
export const deviceFeedsQuery = (api: ApiClient, deviceId: string) =>
  queryOptions({
    queryKey: queryKeys.deviceFeeds(deviceId),
    queryFn: () => api.get<Page<TargetBinding>>(`/devices/${deviceId}/feeds`),
  });

/** The tenant's ingest pipeline over the last day. */
export const ingestSummaryQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: queryKeys.ingestSummary,
    queryFn: () => api.get<IngestSummary>('/ingest/summary'),
    staleTime: 60_000,
  });

export interface BindTargetVariables {
  sensorId: string;
  target: string;
  input: BindTargetInput;
}

/**
 * A binding changes what the target lists, which device a sensor feeds, the
 * sensor's history (target_bound / target_unbound) and the summary.
 */
function invalidateBinding(queryClient: QueryClient, sensorId: string, deviceId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: sensorKeys.targets(sensorId) }),
    queryClient.invalidateQueries({ queryKey: [...sensorKeys.detail(sensorId), 'events'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.deviceFeeds(deviceId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.ingestSummary }),
  ]);
}

export const bindTargetMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<TargetBinding, Error, BindTargetVariables> => ({
  mutationFn: ({ sensorId, target, input }) =>
    api.put<TargetBinding>(
      `/sensors/${sensorId}/targets/${encodeURIComponent(target)}/binding`,
      input,
    ),
  onSuccess: (_binding, { sensorId, input }) =>
    invalidateBinding(queryClient, sensorId, input.deviceId),
});

export interface UnbindTargetVariables {
  sensorId: string;
  binding: TargetBinding;
}

export const unbindTargetMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<void, Error, UnbindTargetVariables> => ({
  mutationFn: ({ sensorId, binding }) =>
    api.delete(
      `/sensors/${sensorId}/targets/${encodeURIComponent(binding.target)}/binding?collector=${binding.collector}`,
    ),
  onSuccess: (_void, { sensorId, binding }) =>
    invalidateBinding(queryClient, sensorId, binding.deviceId),
});

/** The command history polls while any command is still in flight and
 * stops once every one is terminal. */
export const sensorCommandsQuery = (api: ApiClient, id: string, filter: Pagination = {}) =>
  queryOptions({
    queryKey: sensorKeys.commands(id, filter),
    queryFn: () => api.get<Page<SensorCommand>>(`/sensors/${id}/commands`, { ...filter }),
    refetchInterval: (query) =>
      query.state.data?.items.some(isSensorCommandPending) ? sensorCommandPollMs : false,
  });

/** One command, for a caller waiting on a specific result such as a walk. */
export const sensorCommandQuery = (api: ApiClient, id: string, commandId: string) =>
  queryOptions({
    queryKey: sensorKeys.command(id, commandId),
    queryFn: () => api.get<SensorCommand>(`/sensors/${id}/commands/${commandId}`),
    refetchInterval: (query) =>
      query.state.data && isSensorCommandPending(query.state.data) ? sensorCommandPollMs : false,
  });

/**
 * Creating a sensor returns its enrollment code, which the server shows
 * exactly once. `gcTime: 0` keeps the mutation cache from holding the code
 * after the component that displays it unmounts.
 */
export const createSensorMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<CreatedSensor, Error, SensorInput> => ({
  mutationFn: (input) => api.post<CreatedSensor>('/sensors', input),
  gcTime: 0,
  onSuccess: () => invalidateFleet(queryClient),
});

/** invalidateFleet marks every view that counts or lists sensors stale. */
function invalidateFleet(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: sensorKeys.lists }),
    queryClient.invalidateQueries({ queryKey: sensorKeys.summary }),
  ]);
}

/**
 * A lifecycle change (disable, enable, decommission, re-enroll) changes the
 * sensor's state, appends to its history, and moves it between the fleet's
 * counts, so its detail family (which holds the history) and the fleet views
 * are stale. A disabled or decommissioned relay's children lose their route
 * too, which the list refresh picks up as they drop off.
 */
function invalidateLifecycle(queryClient: QueryClient, id: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: sensorKeys.detail(id) }),
    invalidateFleet(queryClient),
  ]);
}

/** Re-enrolling revokes the sensor's certificates and sets it back to
 * pending, so the sensor itself and every list showing it are stale. */
export const reenrollSensorMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<SensorEnrollment, Error, string> => ({
  mutationFn: (id) => api.post<SensorEnrollment>(`/sensors/${id}/enrollment`),
  gcTime: 0,
  onSuccess: async (_enrollment, id) => {
    await invalidateLifecycle(queryClient, id);
  },
});

/** Disabling suspends a sensor without touching its certificate. */
export const disableSensorMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<unknown, Error, string> => ({
  mutationFn: (id) => api.post<unknown>(`/sensors/${id}/disable`),
  onSuccess: async (_result, id) => {
    await invalidateLifecycle(queryClient, id);
  },
});

export const enableSensorMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<unknown, Error, string> => ({
  mutationFn: (id) => api.post<unknown>(`/sensors/${id}/enable`),
  onSuccess: async (_result, id) => {
    await invalidateLifecycle(queryClient, id);
  },
});

/** Decommissioning revokes every certificate but keeps the sensor's record
 * and history, so unlike delete its detail is re-read, not dropped. */
export const decommissionSensorMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<unknown, Error, string> => ({
  mutationFn: (id) => api.post<unknown>(`/sensors/${id}/decommission`),
  onSuccess: async (_result, id) => {
    await invalidateLifecycle(queryClient, id);
  },
});

export const deleteSensorMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<unknown, Error, string> => ({
  mutationFn: (id) => api.delete<unknown>(`/sensors/${id}`),
  onSuccess: async (_result, id) => {
    // The sensor's own entries are only marked stale, never refetched: the
    // page showing them is about to navigate away, and a refetch would 404.
    // Only the lists, which really did change, are re-read now.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: sensorKeys.detail(id), refetchType: 'none' }),
      invalidateFleet(queryClient),
    ]);
  },
});

export interface QueueSensorCommandInput {
  sensorId: string;
  request: SensorCommandRequest;
}

/** Queueing a command changes only that sensor's command history. */
export const queueSensorCommandMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<SensorCommand, Error, QueueSensorCommandInput> => ({
  mutationFn: ({ sensorId, request }) =>
    api.post<SensorCommand>(`/sensors/${sensorId}/commands`, request),
  // A target.upsert carries a community in its variables; do not let the
  // mutation cache keep it once nothing is observing the mutation.
  gcTime: 0,
  onSuccess: (_command, { sensorId }) =>
    queryClient.invalidateQueries({ queryKey: sensorKeys.commandsAll(sensorId) }),
});

/* --- Findings queue --------------------------------------------------------
 *
 * The findings service's queue, served at /api/v1/findings through the same
 * client and cookie. Not the Kubernetes analysis above, which is the
 * inventory API's own `/k8s/findings`.
 */

/** The filters GET /api/v1/findings accepts, named as it names them. Arrays
 * are sent repeated; `label` entries are `key:value`. Keep in step with
 * findingsservice/internal/httpapi/handlers.go readFilter. */
export interface QueueFindingFilter extends Pagination {
  source?: string[];
  category?: string[];
  severity?: string[];
  status?: string[];
  label?: string[];
  search?: string;
  targetId?: string;
  deviceId?: string;
  overdue?: boolean;
  /** A ClusterMAX assessment scope id: its members' findings, its own
   * cluster's, and fleet-wide ones. */
  scope?: string;
  sort?: 'triage' | 'lastObserved' | 'firstObserved' | 'due' | 'severity';
  desc?: boolean;
}

export const queueFindingsQuery = (api: ApiClient, filter: QueueFindingFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.queue(filter),
    queryFn: ({ signal }) => api.get<Page<QueueFinding>>('/findings', { ...filter }, { signal }),
    staleTime: 15_000,
  });

export const queueFindingQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: queryKeys.queueFinding(id),
    queryFn: ({ signal }) =>
      api.get<QueueFinding>(`/findings/${encodeURIComponent(id)}`, undefined, { signal }),
    staleTime: 15_000,
  });

/** A finding's history, newest first. */
export const findingEventsQuery = (api: ApiClient, id: string) =>
  queryOptions({
    queryKey: queryKeys.queueEvents(id),
    queryFn: ({ signal }) =>
      api.get<Page<FindingEvent>>(
        `/findings/${encodeURIComponent(id)}/events`,
        { limit: 100 },
        { signal },
      ),
    staleTime: 15_000,
  });

export interface TriageInput {
  id: string;
  action: FindingAction;
  /** Required by `resolve`: it decides whether seeing the deviation again
   * reopens the finding. */
  resolution?: FindingResolution;
  note?: string;
  /** The version the operator saw, so a concurrent decision is refused with
   * 409 version_conflict instead of overwritten. */
  expectVersion: number;
}

/** triageFindingMutation records one operator decision. It changes the
 * finding, its history, every list it appears in, and the posture score
 * computed from the backlog, so all four are invalidated. */
export const triageFindingMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<QueueFinding, Error, TriageInput> => ({
  mutationFn: ({ id, action, resolution, note, expectVersion }) =>
    api.post<QueueFinding>(`/findings/${encodeURIComponent(id)}/${action}`, {
      expectVersion,
      ...(note ? { note } : {}),
      ...(resolution ? { resolution } : {}),
    }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.queueRoot }),
      invalidatePosture(queryClient),
      // A decision can move a readiness cell: a risk acceptance still
      // fails it, a suppression makes it at risk.
      queryClient.invalidateQueries({ queryKey: queryKeys.readinessRoot }),
    ]);
  },
});

/* --- Minimum versions (specs 01 and 08) ------------------------------------ */

export const baselineKeys = {
  all: ['baselines'] as const,
  effective: ['baselines', 'effective'] as const,
  summary: ['baselines', 'summary'] as const,
  runtime: ['baselines', 'runtime'] as const,
  overrides: ['baselines', 'overrides'] as const,
  assets: (component: string, runtime: boolean, state: BaselineState | '', offset: number) =>
    ['baselines', runtime ? 'runtime-nodes' : 'assets', component, state, offset] as const,
  timeToPatch: (component: string) => ['baselines', 'time-to-patch', component] as const,
};

/** Rows of the assets endpoints per page. */
export const BASELINE_ASSET_PAGE = 50;

/** The baseline the tenant is judged against: the global revision with its overrides. */
export const effectiveBaselineQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: baselineKeys.effective,
    queryFn: ({ signal }) => api.get<EffectiveBaseline>('/baselines', undefined, { signal }),
    staleTime: 60_000,
  });

/** The Firmware and drivers tab. Judged on request, so a minute old is fine. */
export const baselineSummaryQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: baselineKeys.summary,
    queryFn: ({ signal }) => api.get<BaselineSummary>('/baselines/summary', undefined, { signal }),
    staleTime: 60_000,
  });

/** The Host runtime tab. */
export const runtimeBaselineQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: baselineKeys.runtime,
    queryFn: ({ signal }) =>
      api.get<RuntimeBaselineSummary>('/baselines/runtime', undefined, { signal }),
    staleTime: 60_000,
  });

/** The assets (or, with runtime, the nodes) behind one component's row. */
export const baselineAssetsQuery = (
  api: ApiClient,
  component: string,
  runtime: boolean,
  state: BaselineState | '' = '',
  offset = 0,
) =>
  queryOptions({
    queryKey: baselineKeys.assets(component, runtime, state, offset),
    queryFn: ({ signal }) =>
      api.get<Page<BaselineAsset>>(
        runtime
          ? `/baselines/runtime/${encodeURIComponent(component)}/nodes`
          : `/baselines/${encodeURIComponent(component)}/assets`,
        { limit: BASELINE_ASSET_PAGE, offset, ...(state ? { state } : {}) },
        { signal },
      ),
    staleTime: 60_000,
  });

/** How quickly nodes reached the floor each time a component's floor was raised. */
export const timeToPatchQuery = (api: ApiClient, component: string) =>
  queryOptions({
    queryKey: baselineKeys.timeToPatch(component),
    queryFn: ({ signal }) =>
      api.get<TimeToPatch>('/baselines/runtime/time-to-patch', { component }, { signal }),
    staleTime: 5 * 60_000,
  });

export const baselineOverridesQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: baselineKeys.overrides,
    queryFn: ({ signal }) =>
      api.get<BaselineOverrideList>('/baselines/overrides', undefined, { signal }),
    staleTime: 30_000,
  });

export interface SaveBaselineOverrideInput {
  /** Present to replace that override's floor, grace and reason. */
  id?: string;
  create?: BaselineOverrideCreate;
  update?: BaselineOverrideUpdate;
}

/** An override changes the effective baseline and so every number judged against it. */
export const saveBaselineOverrideMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<unknown, Error, SaveBaselineOverrideInput> => ({
  mutationFn: ({ id, create, update }) =>
    id === undefined
      ? api.post('/baselines/overrides', create)
      : api.put(`/baselines/overrides/${encodeURIComponent(id)}`, update),
  onSuccess: () => queryClient.invalidateQueries({ queryKey: baselineKeys.all }),
});

export const deleteBaselineOverrideMutation = (
  api: ApiClient,
  queryClient: QueryClient,
): UseMutationOptions<unknown, Error, string> => ({
  mutationFn: (id) => api.delete(`/baselines/overrides/${encodeURIComponent(id)}`),
  onSuccess: () => queryClient.invalidateQueries({ queryKey: baselineKeys.all }),
});

/* --- Vulnerabilities -------------------------------------------------------
 *
 * Known CVEs matched to what the tenant runs (docs/features/clustermax/
 * 02-cve-matching.md). The API judges the inventory itself, so a CVE whose
 * findings were triaged as false positive still appears here; the page reads
 * the findings queue (queueFindingsQuery with `label=cve:<id>`) for triage.
 */

/** The filters GET /api/v1/vulnerabilities accepts. Keep in step with
 * backend/internal/httpapi/vulnerabilities.go readVulnFilter. */
export interface VulnerabilityListFilter extends Pagination {
  /** Only CVEs on CISA's Known Exploited Vulnerabilities list. */
  kev?: boolean;
  severity?: string[];
  q?: string;
}

export const vulnerabilitiesQuery = (api: ApiClient, filter: VulnerabilityListFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.vulnerabilities(filter),
    queryFn: ({ signal }) => {
      const { severity, ...rest } = filter;
      // The API takes severity as one comma-separated value.
      return api.get<Page<Vulnerability>>(
        '/vulnerabilities',
        { ...rest, ...(severity && severity.length > 0 ? { severity: severity.join(',') } : {}) },
        { signal },
      );
    },
    staleTime: 60_000,
  });

export const vulnerabilitySummaryQuery = (api: ApiClient) =>
  queryOptions({
    queryKey: queryKeys.vulnerabilitySummary,
    queryFn: ({ signal }) =>
      api.get<VulnerabilitySummary>('/vulnerabilities/summary', undefined, { signal }),
    staleTime: 60_000,
  });

export const vulnerabilityQuery = (api: ApiClient, cveId: string) =>
  queryOptions({
    queryKey: queryKeys.vulnerability(cveId),
    queryFn: ({ signal }) =>
      api.get<VulnerabilityDetail>(`/vulnerabilities/${encodeURIComponent(cveId)}`, undefined, {
        signal,
      }),
    staleTime: 60_000,
  });

/* --- ClusterMAX readiness (spec 11) ----------------------------------------
 *
 * Served by the findings service, which the proxy routes
 * /api/v1/clustermax/readiness and /api/v1/clustermax/criteria to. Computed on
 * read from the evaluator's last context and the findings; the evaluator
 * pushes every quarter hour, so a minute's staleness costs nothing.
 */

/** The criterion map of an edition: static per release. */
export const clustermaxCriteriaQuery = (api: ApiClient, edition = '3.0') =>
  queryOptions({
    queryKey: queryKeys.clustermaxCriteria(edition),
    queryFn: ({ signal }) =>
      api.get<ClusterMaxEdition>('/clustermax/criteria', { edition }, { signal }),
    staleTime: Infinity,
  });

/** Every scope, every cell, the fleet's worst and its trend. A tenant that
 * has not enabled readiness answers 409 clustermax_disabled. */
export const fleetReadinessQuery = (api: ApiClient, days: PosturePeriod = 30) =>
  queryOptions({
    queryKey: queryKeys.readiness(days),
    queryFn: ({ signal }) => api.get<FleetReadiness>('/clustermax/readiness', { days }, { signal }),
    staleTime: 60_000,
  });

export const scopeReadinessQuery = (api: ApiClient, scopeId: string, days: PosturePeriod = 30) =>
  queryOptions({
    queryKey: queryKeys.scopeReadiness(scopeId, days),
    queryFn: ({ signal }) =>
      api.get<ScopeReadiness>(
        `/clustermax/readiness/${encodeURIComponent(scopeId)}`,
        { days },
        { signal },
      ),
    staleTime: 60_000,
  });

/** One cell, with the findings that drive it and the evidence held. */
export const criterionReadinessQuery = (api: ApiClient, scopeId: string, criterionId: string) =>
  queryOptions({
    queryKey: queryKeys.criterionReadiness(scopeId, criterionId),
    queryFn: ({ signal }) =>
      api.get<CriterionReadiness>(
        `/clustermax/readiness/${encodeURIComponent(scopeId)}/criteria/${encodeURIComponent(criterionId)}`,
        undefined,
        { signal },
      ),
    staleTime: 60_000,
  });
